"use client";
import PrimaryBtn from "@/components/PrimaryBtn";
import { Icons } from "@/app/assets/shared";
import Image from "next/image";
import { useEffect, useLayoutEffect, useRef } from "react";

interface AboutSectionProps {
  description: string;
  descList: string[];
}

interface StatItem {
  icon: string | undefined;
  value: string;
  label: string;
}

const stats: StatItem[] = [
  {
    icon: Icons?.AboutUsNewIcons?.aboutUsNew1,
    value: "150+",
    label: "Experts in Our Team",
  },
  {
    icon: Icons?.AboutUsNewIcons?.aboutUsNew2,
    value: "23+",
    label: "Years of Experience",
  },
  {
    icon: Icons?.AboutUsNewIcons?.aboutUsNew3,
    value: "1500+",
    label: "Happy Clients",
  },
  {
    icon: Icons?.AboutUsNewIcons?.aboutUsNew4,
    value: "99%",
    label: "Customer Satisfaction",
  },
];

// Border classes for each quadrant of the 2x2 grid:
// Box 1 (top-left): right + bottom border
// Box 2 (top-right): left + bottom border
// Box 3 (bottom-left): top + right border
// Box 4 (bottom-right): top + left border
const borderClasses = [
  "border-r border-b",
  "border-l border-b",
  "border-t border-r",
  "border-t border-l",
];

const borderColorClass = "border-white hover:border-[#225bd4]";

export default function HeroMainContent({
  description,
  descList,
}: AboutSectionProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    const element = sectionRef.current;
    if (!element || typeof window === "undefined" || window.innerWidth < 1024)
      return;
    import("gsap").then(({ default: gsap }) => {
      gsap.set(
        element.querySelectorAll(".hero-title, .hero-paragraph, .hero-other"),
        {
          y: 24,
          opacity: 1,
        },
      );
      gsap.set(element.querySelectorAll(".hero-stat-box"), {
        y: 24,
        opacity: 0,
      });
    });
  }, []);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;
    let mm: any;
    let ctx: any;
    let cancelled = false;

    (async () => {
      const [gsapModule, ScrollTriggerModule] = await Promise.all([
        import("gsap"),
        import("gsap/ScrollTrigger"),
      ]);
      if (cancelled) return;
      const gsap = gsapModule.default;
      const ScrollTrigger = ScrollTriggerModule.default;
      gsap.registerPlugin(ScrollTrigger);
      mm = gsap.matchMedia();
      ctx = gsap.context(() => {
        mm.add("(min-width: 1024px)", () => {
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: element,
              start: "top 80%",
              end: "top 50%",
              scrub: 0.2,
              invalidateOnRefresh: true,
            },
          });

          tl.to(".hero-title", {
            y: 0,
            opacity: 1,
            duration: 1,
            ease: "power2.out",
          });

          tl.to(
            ".hero-paragraph",
            {
              y: 0,
              opacity: 1,
              duration: 0.8,
              ease: "power2.out",
            },
            "-=0.2",
          );

          const otherItems = gsap.utils.toArray<HTMLElement>(".hero-other");
          if (otherItems.length) {
            tl.to(
              otherItems,
              {
                y: 0,
                opacity: 1,
                duration: 0.6,
                ease: "power2.out",
                stagger: 0.15,
              },
              "-=0.1",
            );
          }

          const statBoxes = gsap.utils.toArray<HTMLElement>(".hero-stat-box");
          if (statBoxes.length) {
            tl.to(
              statBoxes,
              {
                y: 0,
                opacity: 1,
                duration: 0.6,
                ease: "power2.out",
                stagger: 0.1,
              },
              "-=0.2",
            );
          }

          return () => {
            tl.scrollTrigger?.kill();
            tl.kill();
          };
        });
      }, sectionRef);
    })();

    return () => {
      cancelled = true;
      ctx?.revert?.();
      mm?.revert?.();
    };
  }, []);

  return (
    <div ref={sectionRef}>
      <div className="flex flex-col lg:flex-row lg:items-center justify-start pt-[0px] lg:pt-[0px] gap-[0px] lg:gap-[48px] lg:mb-2">
        <p className="justify-center text-[#636363] text-[length:var(--primarybtn)] font-medium uppercase tracking-tight pb-5 sm:pb-0">
          About Us
        </p>
      </div>

      <h2 className="hero-title justify-start max-w-[26ch] text-[length:var(--section-title)] leading-[90%] translate-y-0 opacity-100 lg:translate-y-6 lg:opacity-100 pb-5 sm:pb-0">
        <span className="text-[#010101] text-[length:var(--section-title)] font-medium clash-font capitalize leading-[110%]">
          We Deliver AI-Driven Solutions to Help Businesses Achieve
        </span>
        <span className="text-[#225bd4] text-[length:var(--section-title)] font-medium clash-font capitalize leading-[110%]">
          {" "}
          Digital Excellence.
        </span>
      </h2>

      <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-[20px] 2xl:gap-[150px] mt-0 lg:mt-[50px] 2xl:mt-0">
        {/* Left side */}
        <div className="self-stretch inline-flex flex-col justify-start items-start gap-4 lg:gap-9 2xl:pt-[42px] w-full">
          <div className="hero-paragraph self-stretch justify-start text-[#00040b]/70 text-[14px] lg:text-[length:var(--hero-para)] font-normal leading-[150%] translate-y-0 opacity-100 lg:translate-y-6 lg:opacity-100">
            {description}
          </div>

          <div className="hero-other justify-start text-[#00040b]/70 text-[14px] lg:text-[length:var(--hero-list)] leading-[180%] translate-y-0 opacity-100 lg:translate-y-6 lg:opacity-100">
            {descList?.map((item: any, index: number) => (
              <div key={index + 1} className="flex items-center gap-2">
                <Image
                  src={Icons?.dot}
                  className="d-inline-block shrink-0"
                  alt="check"
                  width={24}
                  height={24}
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  fetchPriority="high"
                  priority
                />
                <span>{item}</span>
              </div>
            ))}
          </div>

          <PrimaryBtn
            text="Learn More About Us"
            dark={false}
            iconSrc={Icons?.chevron}
            styleClass="hero-other translate-y-0 opacity-100 lg:translate-y-6 lg:opacity-100 pb-5 sm:pb-0"
            href="/about-us"
          />
        </div>

        {/* Right side */}
        <div className="grid grid-cols-2 w-full">
          {stats.map((stat, index) => (
            <div
              key={stat.label}
              className={`hero-stat-box ${borderClasses[index]} ${borderColorClass} flex flex-col items-center justify-center text-center gap-2 sm:gap-3 p-4 sm:p-6 lg:p-8 transition-colors duration-300 ease-out`}
            >
              {stat.icon && (
                <Image
                  src={stat.icon}
                  alt={stat.label}
                  width={34}
                  height={34}
                  className="w-9 h-9 sm:w-11 sm:h-11 lg:w-14 lg:h-14 object-contain"
                />
              )}

              <div className="parent mt-1 sm:mt-3 space-y-1 sm:space-y-2">
                <div className="text-[#010101] text-xl sm:text-2xl lg:text-3xl font-semibold clash-font leading-tight">
                  {stat.value}
                </div>

                <div className="text-[#00040b] text-xs sm:text-sm lg:text-[18px] leading-[130%]">
                  {stat.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
